import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { de as deLocale, fr as frLocale } from "date-fns/locale";
import { CalendarIcon, Check, ArrowLeft, ArrowRight } from "lucide-react";

const SERVICE_KEYS = ["reinigungsservice", "serviceTeam", "security"];

export default function InquiryPage() {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [altDate, setAltDate] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const locale = lang === "fr" ? frLocale : deLocale;
  useSeo("Anfrage", "Demande", "Sendet Our Promise Events eine unverbindliche Anfrage.", "Envoyez une demande sans engagement à Our Promise Events.");

  const [form, setForm] = useState({
    event_type: "", location: "", guests: "", package: "", services: [],
    budget: "", name: "", email: "", phone: "", message: "", consent: false,
  });

  useEffect(() => {
    const pkg = params.get("package");
    const svc = params.get("service");
    setForm((f) => ({
      ...f,
      package: pkg && ["bronze", "silber", "gold"].includes(pkg) ? pkg : f.package,
      services: svc && SERVICE_KEYS.includes(svc) ? [svc] : f.services,
    }));
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (key) =>
    setForm((f) => ({ ...f, services: f.services.includes(key) ? f.services.filter((s) => s !== key) : [...f.services, key] }));

  const steps = t("inquiry.steps");
  const eventTypes = t("inquiry.eventTypes");
  const serviceName = (key) => t(`services.list.${key}`).name;

  const canNext = () => {
    if (step === 0) return !!form.event_type;
    if (step === 1) return !!date && !!form.location && !!form.guests;
    return true;
  };

  const submit = async () => {
    if (!form.consent) { toast.error(t("inquiry.consentRequired")); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        event_type: form.event_type,
        wedding_date: date ? format(date, "yyyy-MM-dd") : "",
        alt_date: altDate ? format(altDate, "yyyy-MM-dd") : "",
        location: form.location,
        guests: parseInt(form.guests || "0", 10),
        package: form.package,
        services: form.services.map(serviceName),
        budget: form.budget,
        name: form.name, email: form.email, phone: form.phone, message: form.message,
        lang,
      });
      setSuccess(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="inquiry-page">
        <PageHero overline={t("inquiry.overline")} title={t("inquiry.successTitle")} />
        <section className="bg-cream py-24 px-6">
          <div data-testid="inquiry-success" className="max-w-xl mx-auto bg-secondary/40 border border-gold p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full border border-gold flex items-center justify-center text-gold"><Check size={22} /></div>
            <p className="text-ink-muted leading-relaxed">{t("inquiry.success")}</p>
            <button onClick={() => { setSuccess(false); setStep(0); }} data-testid="inquiry-new" className="mt-8 text-[11px] tracking-[0.3em] uppercase border-b border-ink pb-1 hover:text-gold hover:border-gold">
              {t("inquiry.newInquiry")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="inquiry-page">
      <PageHero overline={t("inquiry.overline")} title={t("inquiry.title")} subtitle={t("inquiry.sub")} />

      <section className="bg-cream py-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-12">
            {steps.map((s, i) => (
              <div key={i} className="flex-1 flex items-center">
                <div className={`flex flex-col items-center ${i <= step ? "text-ink" : "text-ink-muted/50"}`}>
                  <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-sm ${i <= step ? "border-gold bg-gold text-ink" : "border-border"}`}>{i + 1}</div>
                  <span className="text-[10px] tracking-widest uppercase mt-2 hidden sm:block">{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-gold" : "bg-border"}`} />}
              </div>
            ))}
          </div>

          <form data-testid="inquiry-form" onSubmit={(e) => e.preventDefault()} className="space-y-8">
            {/* Step 0: event type */}
            {step === 0 && (
              <div className="space-y-4" data-testid="inquiry-step-0">
                <label className="text-xs tracking-[0.2em] uppercase text-ink-muted">{t("inquiry.eventType")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {eventTypes.map((et) => (
                    <button
                      key={et}
                      type="button"
                      data-testid={`inquiry-eventtype-${et}`}
                      onClick={() => set("event_type", et)}
                      className={`py-4 px-3 text-sm border transition-colors ${form.event_type === et ? "border-gold bg-gold/10 text-ink" : "border-border text-ink-muted hover:border-ink"}`}
                    >
                      {et}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: details */}
            {step === 1 && (
              <div className="space-y-8" data-testid="inquiry-step-1">
                <div className="grid sm:grid-cols-2 gap-6">
                  <DateField label={t("inquiry.date")} value={date} onSelect={setDate} locale={locale} placeholder={t("inquiry.pickDate")} testId="inquiry-date" min />
                  <DateField label={`${t("inquiry.altDate")} (${t("common.optional")})`} value={altDate} onSelect={setAltDate} locale={locale} placeholder={t("inquiry.pickDate")} testId="inquiry-altdate" min />
                </div>
                <Input label={t("inquiry.location")} value={form.location} onChange={(e) => set("location", e.target.value)} testId="inquiry-location" />
                <Input label={t("inquiry.guests")} type="number" value={form.guests} onChange={(e) => set("guests", e.target.value)} testId="inquiry-guests" />
              </div>
            )}

            {/* Step 2: wishes */}
            {step === 2 && (
              <div className="space-y-8" data-testid="inquiry-step-2">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-3 block">{t("inquiry.package")}</label>
                  <Select value={form.package} onValueChange={(v) => set("package", v)}>
                    <SelectTrigger data-testid="inquiry-package-trigger" className="border-0 border-b border-border bg-transparent rounded-none px-0 h-auto py-3 focus:ring-0"><SelectValue placeholder={t("inquiry.selectPackage")} /></SelectTrigger>
                    <SelectContent className="bg-cream">
                      <SelectItem value="bronze">{t("packages.items.bronze").name}</SelectItem>
                      <SelectItem value="silber">{t("packages.items.silber").name}</SelectItem>
                      <SelectItem value="gold">{t("packages.items.gold").name}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-3 block">{t("inquiry.servicesLabel")}</label>
                  <div className="space-y-3">
                    {SERVICE_KEYS.map((key) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer" data-testid={`inquiry-service-${key}`}>
                        <Checkbox checked={form.services.includes(key)} onCheckedChange={() => toggleService(key)} className="border-ink data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                        <span className="text-sm">{serviceName(key)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Input label={`${t("inquiry.budget")} (${t("common.optional")})`} value={form.budget} onChange={(e) => set("budget", e.target.value)} testId="inquiry-budget" />
              </div>
            )}

            {/* Step 3: contact + summary */}
            {step === 3 && (
              <div className="space-y-8" data-testid="inquiry-step-3">
                <Input label={t("inquiry.name")} value={form.name} onChange={(e) => set("name", e.target.value)} testId="inquiry-name" required />
                <Input label={t("inquiry.email")} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} testId="inquiry-email" required />
                <Input label={t("inquiry.phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} testId="inquiry-phone" required />
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("inquiry.message")}</label>
                  <textarea data-testid="inquiry-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink resize-none" />
                </div>

                <div className="bg-secondary/40 border border-border p-6 text-sm space-y-1">
                  <p className="text-xs tracking-[0.2em] uppercase text-gold mb-3">{t("inquiry.summaryTitle")}</p>
                  <p>{t("inquiry.eventType")}: <strong>{form.event_type || "—"}</strong></p>
                  <p>{t("inquiry.date")}: <strong>{date ? format(date, "PPP", { locale }) : "—"}</strong></p>
                  <p>{t("inquiry.location")}: <strong>{form.location || "—"}</strong> · {form.guests || "—"} {t("inquiry.guests")}</p>
                  <p>{t("inquiry.package")}: <strong>{form.package ? t(`packages.items.${form.package}`).name : t("inquiry.noPackage")}</strong></p>
                  {form.services.length > 0 && <p>Services: <strong>{form.services.map(serviceName).join(", ")}</strong></p>}
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="inquiry-consent-label">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="inquiry-consent" className="border-ink data-[state=checked]:bg-gold data-[state=checked]:border-gold mt-0.5" />
                  <span className="text-sm text-ink-muted">{t("inquiry.consent")}</span>
                </label>
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex justify-between pt-6">
              <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="inquiry-back" className={`flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase ${step === 0 ? "opacity-0 pointer-events-none" : "hover:text-gold"}`}>
                <ArrowLeft size={14} /> {t("common.back")}
              </button>
              {step < steps.length - 1 ? (
                <button type="button" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error(t("common.required"))} data-testid="inquiry-next" className="flex items-center gap-2 bg-ink text-cream px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-ink transition-colors">
                  {t("common.next")} <ArrowRight size={14} />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={submitting} data-testid="inquiry-submit" className="bg-gold text-ink px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-ink hover:text-cream transition-colors disabled:opacity-60">
                  {submitting ? t("common.sending") : t("inquiry.submit")}
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Input({ label, testId, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={onChange} required={required} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink transition-colors" />
    </div>
  );
}

function DateField({ label, value, onSelect, locale, placeholder, testId, min }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testId}-trigger`} className="flex items-center justify-between border-b border-border bg-transparent text-left py-3 hover:border-ink transition-colors">
            <span className={value ? "" : "text-ink-muted"}>{value ? format(value, "PPP", { locale }) : placeholder}</span>
            <CalendarIcon size={18} className="text-ink-muted" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-cream border-border" align="start">
          <Calendar mode="single" selected={value} onSelect={onSelect} locale={locale} disabled={min ? (d) => d < new Date(new Date().setHours(0, 0, 0, 0)) : undefined} data-testid={`${testId}-calendar`} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
